import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  EyeIcon,
  UserIcon,
  BriefcaseIcon,
  CalendarIcon,
  StarIcon
} from '@heroicons/react/24/outline';
import { API_BASE } from '../lib/config';

const API = API_BASE;
const APIv2 = `${API_BASE}`;

const ApplicationsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchApplications();
  }, []);

  useEffect(() => {
    if (user) {
      fetchApplications();
    }
  }, [user]);

  const fetchApplications = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const response = await axios.get(`${API}/matching-applications/my`, { headers });
      setApplications(response.data);
    } catch (error) {
      toast.error('マッチング情報の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const updateApplicationStatus = async (applicationId, status, jobId) => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      // 事業者で accepted の場合は新APIを優先
      if (user?.user_type === 'employer' && status === 'accepted' && jobId) {
        try {
          const url = `${APIv2}/jobs/${jobId}/applications/${applicationId}/accept`;
          const res = await axios.post(url, null, { headers });
          if (res.status >= 200 && res.status < 300) {
            toast.success('採用を確定しました');
            fetchApplications();
            return;
          }
        } catch (_) {
          // フォールバックへ
        }
      }
      await axios.put(`${API}/matching-applications/${applicationId}/status?status=${status}`, null, { headers });
      toast.success('マッチング状況を更新しました');
      fetchApplications();
    } catch (error) {
      toast.error('マッチング状況の更新に失敗しました');
    }
  };

  const rebalanceJob = async (jobId) => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const url = `${APIv2}/jobs/${jobId}/rebalance`;
      const res = await axios.post(url, null, { headers });
      if (res.status >= 200 && res.status < 300) {
        toast.success('空き枠の自動充足を実行しました');
        fetchApplications();
      } else {
        toast.error('自動充足の実行に失敗しました');
      }
    } catch (e) {
      toast.error('自動充足の実行に失敗しました');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { class: 'status-badge status-pending', label: '選考中' },
      accepted: { class: 'status-badge status-accepted', label: 'マッチング成立' },
      rejected: { class: 'status-badge status-rejected', label: '不成立' },
      completed: { class: 'status-badge status-completed', label: '完了' }
    };
    
    const badge = badges[status] || badges.pending;
    return <span className={badge.class}>{badge.label}</span>;
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: <ClockIcon className="w-5 h-5 text-yellow-500" />,
      accepted: <CheckCircleIcon className="w-5 h-5 text-green-500" />,
      rejected: <XCircleIcon className="w-5 h-5 text-red-500" />,
      completed: <StarIcon className="w-5 h-5 text-purple-500" />
    };
    
    return icons[status] || icons.pending;
  };

  const filteredApplications = applications.filter(app => {
    if (filter === 'all') return true;
    return app.status === filter;
  });

  const getStatsForEmployer = () => {
    const stats = {
      total: applications.length,
      pending: applications.filter(app => app.status === 'pending').length,
      accepted: applications.filter(app => app.status === 'accepted').length,
      rejected: applications.filter(app => app.status === 'rejected').length,
      completed: applications.filter(app => app.status === 'completed').length
    };
    return stats;
  };

  const getStatsForJobSeeker = () => {
    const stats = {
      total: applications.length,
      pending: applications.filter(app => app.status === 'pending').length,
      accepted: applications.filter(app => app.status === 'accepted').length,
      rejected: applications.filter(app => app.status === 'rejected').length,
      completed: applications.filter(app => app.status === 'completed').length
    };
    return stats;
  };

  const stats = user?.user_type === 'employer' ? getStatsForEmployer() : getStatsForJobSeeker();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-64 mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-96 mb-8"></div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="bg-white p-6 rounded-lg shadow-sm">
                  <div className="h-4 bg-gray-200 rounded w-16 mb-2"></div>
                  <div className="h-8 bg-gray-200 rounded w-8"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {user?.user_type === 'employer' ? 'ワーカー管理' : 'マッチング状況'}
          </h1>
          <p className="text-gray-600">
            {user?.user_type === 'employer' 
              ? 'お仕事への参加希望者を管理し、マッチングプロセスを進めましょう'
              : 'あなたのマッチング状況を確認し、進捗を把握しましょう'
            }
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <div className="text-sm font-medium text-gray-500 mb-1">総申込数</div>
            <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <div className="text-sm font-medium text-yellow-500 mb-1">選考中</div>
            <div className="text-2xl font-bold text-yellow-600">{stats.pending}</div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <div className="text-sm font-medium text-green-500 mb-1">マッチング成立</div>
            <div className="text-2xl font-bold text-green-600">{stats.accepted}</div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <div className="text-sm font-medium text-red-500 mb-1">不成立</div>
            <div className="text-2xl font-bold text-red-600">{stats.rejected}</div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <div className="text-sm font-medium text-purple-500 mb-1">完了</div>
            <div className="text-2xl font-bold text-purple-600">{stats.completed}</div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <div className="flex flex-wrap gap-2">
            {[
              { key: 'all', label: 'すべて' },
              { key: 'pending', label: '選考中' },
              { key: 'accepted', label: 'マッチング成立' },
              { key: 'rejected', label: '不成立' },
              { key: 'completed', label: '完了' }
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setFilter(key)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filter === key
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {label} ({key === 'all' ? stats.total : stats[key]})
              </button>
            ))}
          </div>
        </div>

        {/* Applications List */}
        {filteredApplications.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <div className="text-gray-400 text-6xl mb-4"></div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {filter === 'all' ? '申込がありません' : `${filter === 'pending' ? '選考中' : filter === 'accepted' ? 'マッチング成立' : filter === 'rejected' ? '不成立' : '完了'}の申込がありません`}
            </h3>
            <p className="text-gray-600 mb-6">
              {user?.user_type === 'employer' 
                ? 'お仕事が投稿されると、参加希望者の情報がここに表示されます'
                : 'お仕事に申し込みすると、マッチング状況がここに表示されます'
              }
            </p>
            <Link
              to="/jobs"
              className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              {user?.user_type === 'employer' ? 'お仕事を投稿' : 'お仕事を探す'}
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredApplications.map((application) => (
              <div key={application.id} className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 mt-1">
                      {getStatusIcon(application.status)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {application.job_title}
                        </h3>
                        {getStatusBadge(application.status)}
                      </div>
                      
                      <div className="flex items-center space-x-4 text-sm text-gray-600 mb-3">
                        <div className="flex items-center">
                          {user?.user_type === 'employer' ? (
                            <>
                              <UserIcon className="w-4 h-4 mr-1" />
                              参加希望者: {application.applicant_name}
                            </>
                          ) : (
                            <>
                              <BriefcaseIcon className="w-4 h-4 mr-1" />
                              依頼者: {application.employer_name || '未設定'}
                            </>
                          )}
                        </div>
                        <div className="flex items-center">
                          <CalendarIcon className="w-4 h-4 mr-1" />
                          申込日: {formatDate(application.applied_at)}
                        </div>
                      </div>

                      {application.message && (
                        <div className="bg-gray-50 p-3 rounded-lg mb-3">
                          <p className="text-sm text-gray-700">
                            <span className="font-medium">申込メッセージ:</span> {application.message}
                          </p>
                        </div>
                      )}

                      {application.preferred_work_start_time && application.preferred_work_end_time && (
                        <div className="bg-blue-50 p-3 rounded-lg mb-3">
                          <p className="text-sm text-blue-700">
                            <span className="font-medium">希望勤務時間:</span> {application.preferred_work_start_time} - {application.preferred_work_end_time}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Link
                      to={`/jobs/${application.job_id}`}
                      className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center"
                    >
                      <EyeIcon className="w-4 h-4 mr-1" />
                      詳細
                    </Link>
                  </div>
                </div>

                {/* Matching Actions */}
                {user?.user_type === 'employer' && application.status === 'pending' && (
                  <div className="flex space-x-3 pt-4 border-t border-gray-200">
                    <button
                      onClick={() => updateApplicationStatus(application.id, 'accepted', application.job_id)}
                      className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-700 transition-colors flex items-center"
                    >
                      <CheckCircleIcon className="w-4 h-4 mr-1" />
                      マッチング成立
                    </button>
                    <button
                      onClick={() => rebalanceJob(application.job_id)}
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors flex items-center"
                    >
                      空き枠自動充足
                    </button>
                    <button
                      onClick={() => updateApplicationStatus(application.id, 'rejected')}
                      className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-700 transition-colors flex items-center"
                    >
                      <XCircleIcon className="w-4 h-4 mr-1" />
                      不成立
                    </button>
                  </div>
                )}

                {/* Message Function */}
                {application.status === 'accepted' && (
                  <div className="flex space-x-3 pt-4 border-t border-gray-200">
                    <button
                      onClick={() => navigate(`/messages/${application.id}`)}
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors flex items-center"
                    >
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                      メッセージ
                    </button>
                  </div>
                )}

                {/* Job Completion */}
                {user?.user_type === 'employer' && application.status === 'accepted' && (
                  <div className="flex space-x-3 pt-4 border-t border-gray-200">
                    <button
                      onClick={() => updateApplicationStatus(application.id, 'completed')}
                      className="bg-purple-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors flex items-center"
                    >
                      <StarIcon className="w-4 h-4 mr-1" />
                      お仕事完了
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ApplicationsPage;